import {
  ETATS_RECOMPENSE,
  type EtatRecompenseTwitch,
  RARETES_TIRABLES,
  type RareteTirable,
  SOURCES_BOOSTER,
  type SourceBooster,
} from "../contract";
import { detailDe, issueIncertaineDuDetail } from "../diffusion-api";
import { erreurText } from "./cartes-texts";

/**
 * Les phrases de l'ecran « Boosters » de la regie (ticket 6 du TCG, PR 4). Sobres, au constat ou a l'imperatif.
 * **Rien du serveur n'est affiche tel quel** : un code d'erreur, une source, un etat n'ont que leur phrase, ecrite ici,
 * en `Record<Code, texte>` pour chaque liste fermee. Les noms des viewers et les motifs viennent des donnees et sont
 * rendus comme du texte.
 */

export const BOOSTERS_PATH = "/regie/cartes/boosters";

/** Les tirages limites sont des constantes du serveur (`TIRAGES_LIMITES`, decision 2 du plan) : l'ecran les dit. */
export const TIRAGES_LIMITES_AFFICHES: Readonly<Partial<Record<RareteTirable, number>>> = {
  seigneur: 30,
  legende: 10,
};

export const SOURCES: Record<SourceBooster, string> = {
  presence: "Présence",
  follow: "Follow",
  sub: "Sub",
  sub_offert: "Sub offert",
  bits: "Bits",
  points: "Récompense de points",
  recyclage: "Recyclage",
  don_regie: "Offert par le château",
};

export const ETATS_TWITCH: Record<EtatRecompenseTwitch, string> = {
  a_creer: "pas encore créée chez Twitch",
  a_pousser: "en cours d'envoi chez Twitch",
  a_jour: "à jour",
  refusee: "refusée : la chaîne doit être affiliée",
  pleine: "bloquée : la chaîne a déjà 50 récompenses",
  titre_pris: "bloquée : ce titre est déjà pris sur la chaîne",
};

export const BOOSTERS = {
  titre: "Boosters",
  chargement: "Lecture des boosters...",
  lecture: "Les boosters n'ont pas pu être lus. Réessaie dans un instant.",
  relire: "Réessayer",
  totaux: {
    label: "Les pochettes en chiffres",
    dernierStream: "distribués au dernier stream",
    ouvertes: "ouverts",
    scellees: "scellés chez les viewers",
  },
  echecs: (n: number): string =>
    n === 1
      ? "1 rachat de points n'a pas pu être honoré : il est à traiter à la main chez Twitch."
      : `${n} rachats de points n'ont pas pu être honorés : ils sont à traiter à la main chez Twitch.`,
  champ: {
    entier: (min: number, max: number): string => `Un nombre entier de ${min} à ${max}.`,
    chance: "Une chance de 0 à 100, avec une décimale au plus (12,5).",
  },
  gains: {
    titre: "Gains par source",
    aide: "Un changement ne vaut que pour les gains à venir : les pochettes déjà gagnées ne bougent pas.",
    colonnes: { source: "Source", gain: "Gain" },
    presence: {
      nom: "Présence",
      aide: "Un stream compte dès 10 min ou un premier message",
      unite: "par stream",
    },
    follow: { nom: "Follow", aide: "Au premier follow seulement", unite: "par follow" },
    sub: {
      nom: "Sub (nouveau, renouvelé, reçu)",
      aide: "Pour celui qui est sub",
      unite: "par sub",
    },
    subOffert: { nom: "Sub offert", aide: "Pour celui qui offre", unite: "par sub" },
    bits: { nom: "Bits", aide: "Une tranche de bits", unite: "bits par booster" },
    recyclage: {
      nom: "Recyclage",
      aide: "Doublons Écuyer ou Chevalier",
      unite: "doublons par booster",
    },
    raid: "Une carte de raid ou de Moment tombe chez tous les présents : elle n'a pas de réglage de gain.",
    sur30j: (n: number): string => (n === 1 ? "1 sur 30 jours" : `${n} sur 30 jours`),
  },
  recompense: {
    titre: "Récompense de points",
    aide: "Créée par Castellan chez Twitch, désactivée tant que tu ne l'actives pas.",
    nom: "Titre",
    prix: "Prix en points",
    limite: "Limite par stream et par viewer",
    limiteAide: "Laisse vide pour ne pas limiter.",
    active: "Active chez Twitch",
    etat: "Chez Twitch",
    erreurTitre: "Un titre de 1 à 45 caractères.",
    erreurPrix: "Un prix entier de 1 à 1 000 000.",
    erreurLimite: "Vide, ou un nombre entier de 1 à 100.",
    enregistrer: "Enregistrer la récompense",
    enCours: "Enregistrement...",
    fait: "Récompense enregistrée. Castellan la pousse chez Twitch.",
    erreur: "La récompense n'a pas pu être enregistrée. Réessaie dans un instant.",
    rachats: (enCours: number, honores: number, rembourses: number): string =>
      `Rachats : ${enCours} en cours, ${honores} honorés et ${rembourses} remboursés sur 30 jours.`,
    toutes: "Voir toutes les récompenses de la chaîne",
  },
  tirage: {
    titre: "Tirage",
    colonnes: { rarete: "Rareté", chance: "Chance", limite: "Tirage" },
    libre: "libre",
    exemplaires: (n: number): string => `${n} ex.`,
    total: "Total",
    totalOk: "Le total est de 100,0 %.",
    manque: (valeur: string): string => `Il manque ${valeur} %.`,
    trop: (valeur: string): string => `Il y a ${valeur} % de trop.`,
    invalide: "Une des chances n'est pas un nombre valide.",
    faits: "Les faits",
    cartes: "Cartes par booster",
    cartesAide: (cartes: number): string => `${cartes}, la dernière au moins Chevalier`,
    brillante: "Brillante : 1 carte sur",
    chatelain: "Châtelain : 1 booster sur",
    chatelainAide: "Le Châtelain n'existe qu'en un seul exemplaire par saison.",
    enregistrer: "Enregistrer les réglages",
    enCours: "Enregistrement...",
    fait: "Réglages enregistrés. Ils valent pour les gains et les tirages à venir.",
    erreur: "Les réglages n'ont pas pu être enregistrés. Réessaie dans un instant.",
    invalideBouton: "Corrige les champs signalés pour enregistrer.",
  },
  don: {
    titre: "Offrir des boosters",
    viewer: "Viewer",
    viewerAide: "Son login Twitch",
    nombre: "Nombre",
    motif: "Motif",
    motifAide: (n: number): string => `${n} / 80 caractères, montré au viewer sur la pochette`,
    note: "Le viewer les trouvera dans ses boosters.",
    offrir: (n: number): string => (n === 1 ? "Offrir 1 booster" : `Offrir ${n} boosters`),
    enCours: "Envoi...",
    fait: (n: number, login: string): string =>
      n === 1 ? `1 booster offert à ${login}.` : `${n} boosters offerts à ${login}.`,
    erreurLogin: "Écris le login du viewer.",
    erreurNombre: "Un nombre entier de 1 à 10.",
    erreurMotif: "Écris un motif de 80 caractères au plus.",
    codes: {
      viewer_inconnu:
        "Aucun viewer ne peut recevoir ce don : le login est inconnu, ou la personne ne peut pas en recevoir.",
      motif_refuse:
        "Le motif ne passe pas les contrôles : une ligne, sans arobase, adresse, tiret long ni émoji.",
      incertain:
        "Le don a peut-être abouti : regarde le journal. Le renvoyer tel quel ne le double pas.",
    },
  },
  simulation: {
    titre: "Simulation",
    aide: "Tire 10 000 boosters en mémoire sur la réserve et les réglages du moment. Rien n'est écrit ni consommé.",
    lancer: "Simuler 10 000 boosters",
    enCours: "Simulation en cours...",
    resultat: (n: number, cartes: number): string =>
      `${n.toLocaleString("fr-FR")} boosters simulés, ${cartes.toLocaleString("fr-FR")} cartes.`,
    colonnes: { rarete: "Rareté", obtenu: "Obtenu", chance: "Chance" },
    chatelain: (n: number): string =>
      n === 0
        ? "Le Châtelain n'est sorti dans aucun booster."
        : n === 1
          ? "Le Châtelain est sorti dans 1 booster."
          : `Le Châtelain est sorti dans ${n} boosters.`,
    brillantes: (n: number): string => (n === 1 ? "1 carte brillante" : `${n} cartes brillantes`),
    replis: (descentes: number, remontees: number): string =>
      `Replis : ${descentes} descentes, ${remontees} remontées (une rareté sans personne tire une rareté voisine).`,
    reserve: "Personnes tirables par rareté",
    epuisements: "Tirages limités épuisés",
    aucunEpuisement: "Aucun tirage limité ne s'épuise.",
    epuise: (nom: string, apres: number): string =>
      `${nom} : épuisé après ${apres.toLocaleString("fr-FR")} boosters`,
    pasDeSaison: "Aucune saison n'est ouverte : il n'y a rien à simuler.",
    erreur: "La simulation n'a pas pu se faire. Réessaie dans un instant.",
  },
  journal: {
    gains: "Derniers gains",
    ouvertures: "Dernières ouvertures",
    videGains: "Aucun gain pour l'instant.",
    videOuvertures: "Aucune ouverture pour l'instant.",
    plus: (n: number): string => `+${n}`,
    gagne: (n: number): string => (n === 1 ? "1 booster gagné" : `${n} boosters gagnés`),
    brillantes: (n: number): string => (n === 1 ? "dont 1 brillante" : `dont ${n} brillantes`),
    offert: (motif: string): string => `Offert par le château : ${motif}`,
  },
  etatVide: {
    titre: "Aucun booster pour l'instant",
    texte:
      "Les pochettes arrivent avec la présence, les follows, les subs et les bits des prochains streams. Tu peux déjà en offrir.",
  },
} as const;

/** Une chance en dixiemes de pour cent, a la francaise : 125 donne « 12,5 ». */
export function pourcent(dixiemes: number): string {
  return (dixiemes / 10).toLocaleString("fr-FR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}

/** La phrase d'un refus du don : son code, sinon la phrase generale des ecrans des cartes. */
export function donErreurText(erreur: unknown): string {
  const detail = detailDe(erreur);
  if (issueIncertaineDuDetail(detail)) return BOOSTERS.don.codes.incertain;
  if (detail.code === "viewer_inconnu") return BOOSTERS.don.codes.viewer_inconnu;
  if (detail.code === "motif_refuse") return BOOSTERS.don.codes.motif_refuse;
  return erreurText(erreur);
}

/** Verifie que chaque liste fermee du contrat a sa phrase (le type l'impose deja ; le test le relit). */
export const LISTES_FERMEES = {
  sources: SOURCES_BOOSTER,
  etats: ETATS_RECOMPENSE,
  raretes: RARETES_TIRABLES,
} as const;
